/**
 * Arrested: a night in a cell, and a way out. What used to be one card ("Getting out cost ₦X") is now a scene
 * with choices, the bail amount shown up front:
 *  - PAY it yourself, if you have it;
 *  - your PHONE CALL: ask someone to bail you out. It's a REQUEST the other person answers (an NPC from how close
 *    you are, family or not, and what they can afford; with multiplayer, a real player answering on their own
 *    screen and paying from their own life: same request, a remote answer). Whoever pays, you owe them.
 *  - Lagos: the officer's SETTLEMENT, cheaper and off the books;
 *  - WAIT it out: days in the cell, released without paying; the shifts you miss are missed.
 * The other person's money is never taken without their yes.
 */
import { advance, message, spend, type LifeEvent, type LifeState } from './life.ts';
import { hereCountry, localAmount, localPrice, nudge } from './character.ts';
import { isFamily, isPartner } from './lifecycle.ts';
import { Rng } from './rng.ts';

export interface Custody {
  since: number;
  /** bail, in this life's money */
  bail: number;
  charge: string;
  /** the station (the game names it) */
  station: string;
  /** phone calls left (one, and a second if the first said no) */
  calls: number;
  /** Lagos: what the officer would take to let it go (null: not offered, or already turned down) */
  settlement: number | null;
  /** days you'd sit if you wait it out */
  waitDays: number;
  /** who's been asked (and said no) */
  asked: string[];
}

const DAY = 1440;
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/** In a cell right now (null: free). */
export const custodyOf = (s: LifeState): Custody | null => s.custody ?? null;

/** Taken in. Bail is set from the charge and what you look like you can pay; a repeat offence costs more and waits longer. */
export function takeIntoCustody(s: LifeState, charge: string, station: string, repeat: boolean): Custody {
  const lagos = hereCountry(s) === 'nigeria';
  const bail = localPrice(s, (repeat ? 150_000 : 60_000) + Math.min(s.money, 2_000_000) * 0.15, 5_000);
  s.custody = { since: s.minutes, bail, charge, station, calls: 2, settlement: lagos ? localAmount(s, bail * 0.4, 5_000) : null, waitDays: repeat ? 5 : 2, asked: [] };
  s.history.push({ t: s.minutes, text: `Arrested ${charge}.` });
  return s.custody;
}

function release(s: LifeState, how: string, events: LifeEvent[]) {
  s.custody = undefined;
  s.history.push({ t: s.minutes, text: how });
  s.needs.energy = Math.max(0, s.needs.energy - 20);
  nudge(s, 'cautious', 0.18, events);
}

/** Pay it yourself. */
export function payBail(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  const c = s.custody;
  if (!c) return { ok: false, line: '', events: [] };
  if (!spend(s, c.bail, 'Bail')) return { ok: false, line: 'You don\'t have it.', events: [] };
  const events = advance(s, 60, { busy: true });
  release(s, 'Paid bail and walked out.', events);
  return { ok: true, line: 'Forms, a stamp, your phone back in a plastic bag. You\'re out.', events };
}

/** Lagos: the officer's settlement. Cheaper, quicker, and nobody writes anything down. */
export function paySettlement(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  const c = s.custody;
  if (!c || c.settlement === null) return { ok: false, line: '', events: [] };
  if (!spend(s, c.settlement, 'Settlement')) return { ok: false, line: '"You no get am? Then siddon there."', events: [] };
  const events = advance(s, 20, { busy: true });
  release(s, 'Settled with the officer and walked out.', events);
  nudge(s, 'honest', -0.02, events);
  return { ok: true, line: 'He counts it twice, without looking at you. "Oya, go. Next time, shine your eye."', events };
}

/** Who can you call: everyone you know, best bets first (family and close friends with means). */
export function bailCandidates(s: LifeState): { name: string; relation: string; closeness: number }[] {
  const c = s.custody;
  return [...s.contacts].filter((p) => p.relation !== 'Ex' && !c?.asked.includes(p.name))
    .sort((a, b) => (isFamily(b.relation) ? 30 : 0) + b.closeness - ((isFamily(a.relation) ? 30 : 0) + a.closeness));
}

/**
 * The request a contact answers: would they bail you out (an NPC decides from closeness, family or partner, what
 * they can likely afford, and whether you've done this before). A real player answers this themselves.
 */
export function npcWouldBail(s: LifeState, name: string): boolean {
  const p = s.contacts.find((x) => x.name === name), c = s.custody;
  if (!p || !c) return false;
  const fam = isFamily(p.relation) || p.relation === 'Wife' || p.relation === 'Husband';
  const means = { struggling: 0.5, working: 0.8, comfortable: 1, wealthy: 1.1 }[s.card.background.id] ?? 0.8; // (your people's means are like yours)
  const again = s.history.filter((h) => /^(Bailed out|Paid bail|Settled)/.test(h.text)).length;
  const k = (p.closeness / 100) * (fam ? 1.25 : isPartner(p.relation) ? 1.1 : 0.75) * means - again * 0.15;
  return new Rng((s.seed ^ Math.imul(s.minutes + name.length * 13, 0x2545f491)) >>> 0).chance(clamp01(k));
}

/**
 * Your call: they answered (`yes` from npcWouldBail, or from a player). Yes: they pay, you're out, you owe them
 * (closeness takes the strain; family lectures; they'll remind you). No: one call fewer.
 */
export function bailCall(s: LifeState, name: string, yes: boolean): { ok: boolean; line: string; events: LifeEvent[] } {
  const c = s.custody, p = s.contacts.find((x) => x.name === name);
  if (!c || !p || c.calls <= 0) return { ok: false, line: '', events: [] };
  c.calls--;
  const events = advance(s, 30, { busy: true });
  const first = name.split(' ')[0], fam = isFamily(p.relation);
  if (!yes) {
    c.asked.push(name);
    p.closeness = Math.max(0, p.closeness - 3);
    return { ok: false, line: fam ? `${first} goes quiet for a long time. "I can't this time. Maybe this will teach you."` : `"${c.bail.toLocaleString()}? I don't have that kind of money," ${first} says. "Sorry."`, events };
  }
  const more = advance(s, 120, { busy: true });
  events.push(...more);
  release(s, `Bailed out by ${name}.`, events);
  p.closeness = Math.max(0, p.closeness + (fam ? -4 : -2));
  (s.owes ??= []).push({ name, amount: c.bail, since: s.minutes });
  // what you owe: a reminder in a few days (and, for a friend, the money back if you can)
  s.inbox.push({ at: s.minutes + 3 * DAY, from: name, text: fam ? 'I hope you\'ve learnt your lesson. We will talk when you come home.' : `You owe me one. And the ${c.bail.toLocaleString()} 😅` });
  return { ok: true, line: fam ? `Two hours later ${first} is at the desk, signing. They don't say anything to you until you're outside.` : `Two hours later ${first} walks in, pays, and shakes their head at you. "You owe me."`, events };
}

/** Wait it out: the days pass in a cell. Released without paying; whatever you'd have done those days, you didn't. */
export function waitItOut(s: LifeState): { line: string; events: LifeEvent[] } {
  const c = s.custody;
  if (!c) return { line: '', events: [] };
  const events = advance(s, c.waitDays * DAY, { busy: true });
  s.needs.hygiene = Math.max(0, s.needs.hygiene - 40);
  s.needs.fun = Math.max(0, s.needs.fun - 30);
  s.health = Math.max(0, s.health - 4);
  const days = c.waitDays;
  release(s, `Released after ${days} days in a cell.`, events);
  return { line: `${days} days. A bench, a bucket, the same four walls. Then a name called and the door open.`, events };
}

/** What you owe someone (0: nothing). */
export const owedTo = (s: LifeState, name: string) => (s.owes ?? []).filter((o) => o.name === name).reduce((t, o) => t + o.amount, 0);

/** Pay back someone who bailed you out (all of it). Closeness back up; paying late still counts, less. */
export function repayBail(s: LifeState, name: string, amount = owedTo(s, name)): boolean {
  if (amount <= 0 || !spend(s, amount, `Paid back ${name.split(' ')[0]}`)) return false;
  const p = s.contacts.find((x) => x.name === name);
  const late = (s.owes ?? []).some((o) => o.name === name && o.nagged);
  s.owes = (s.owes ?? []).filter((o) => o.name !== name);
  if (p) p.closeness = Math.min(100, p.closeness + (late ? 3 : 6));
  const ev: LifeEvent[] = [];
  message(s, name, isFamily(p?.relation ?? '') ? "You didn't have to. But thank you. Stay out of trouble." : late ? 'Finally 😅 Thank you.' : 'Ah, you actually paid me back. Respect 🙏', ev);
  s.history.push({ t: s.minutes, text: `Paid ${name.split(' ')[0]} back the bail.` });
  return true;
}

/** Each day: two weeks unpaid and they ask (once), with the cost to the friendship. */
export function owesDay(s: LifeState, events: LifeEvent[]) {
  for (const o of s.owes ?? []) {
    if (o.nagged || s.minutes - o.since < 14 * DAY) continue;
    o.nagged = true;
    const p = s.contacts.find((x) => x.name === o.name);
    if (p) p.closeness = Math.max(0, p.closeness - 5);
    message(s, o.name, p && isFamily(p.relation) ? "We haven't talked about the bail money." : `Hey… about the ${o.amount.toLocaleString()} from the station. I need it back.`, events);
  }
}
